import { useState, useEffect, useCallback, useMemo } from 'react';

import {
  LoadingOutlined,
  PlusOutlined,
  InboxOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { message, Upload, Image, Button } from 'antd';
const { Dragger } = Upload;

import request from '@/request';

export default function UploadItem({
  label = '',
  value = '',
  require = false,
  placeholder = '',
  multiple = false,
  onChange,
  beforeUpload,
  maxSize = 5, // MB
  accept = 'image/*',
  demoUrl = '',
  demoDownloadUrl = '',
  disabled = false,
}) {
  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [demoShow, setDemoShow] = useState(false);

  useEffect(() => {
    // value 受控清空 fileList
    if (multiple) {
      if (Array.isArray(value)) {
        setFileList(value);
      } else if (!value || value.length === 0) {
        setFileList([]);
      }
    } else {
      if (Array.isArray(value) && value.length > 0) {
        setFileList([
          {
            uid: value[0].uid || 0,
            name:
              value[0].name ||
              (value[0].url ? value[0].url.split('/').pop() : 'file-0'),
            size: value[0].size || 0,
            type: value[0].type || '',
            status: value[0].status || 'done',
            url: value[0].url || '',
          },
        ]);
      } else if (!value || value.length === 0) {
        setFileList([]);
      }
    }
  }, [value, multiple]);

  const handleBeforeUpload = useCallback(
    (file) => {
      // 检查文件类型 - 支持图片和文档
      const isImage = file.type.startsWith('image/');
      const isDocument = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      ].includes(file.type);

      if (!isImage && accept === 'image/*') {
        message.error('只能上传图片文件！');
        return Upload.LIST_IGNORE;
      }

      if (!isImage && !isDocument && accept !== 'image/*') {
        message.error('只能上传图片或文档文件（PDF、Word、Excel、PPT）！');
        return Upload.LIST_IGNORE;
      }

      // 检查文件大小
      const isLtMaxSize = file.size / 1024 / 1024 < maxSize;
      if (!isLtMaxSize) {
        message.error(`文件大小不能超过 ${maxSize}MB！`);
        return Upload.LIST_IGNORE;
      }

      // 自定义校验
      if (beforeUpload) {
        const result = beforeUpload(file);
        // 如果返回 false，阻止上传但显示文件名
        if (result === false) {
          // 手动更新文件列表以显示文件名
          setFileList([
            {
              uid: file.uid || Date.now(),
              name: file.name,
              size: file.size,
              type: file.type,
              status: 'done',
              originFileObj: file,
            },
          ]);
          return Upload.LIST_IGNORE;
        }
        return result;
      }

      return true; // 允许上传
    },
    [accept, maxSize, beforeUpload]
  );

  const handleCustomRequest = useCallback(
    async ({ file, onSuccess, onError }) => {
      try {
        // 1. 获取七牛云上传 token
        const uploadToken = await request.get('/oss/qn/token');

        // 2. 创建 FormData
        const formData = new FormData();
        formData.append('file', file);
        formData.append('token', uploadToken);

        // 3. 上传到七牛云
        const uploadResponse = await fetch('https://up-z2.qiniup.com', {
          method: 'POST',
          body: formData,
        });

        if (!uploadResponse.ok) {
          throw new Error('上传失败');
        }

        const result = await uploadResponse.json();
        // 4. 生成预览 URL
        const fileUrl = `https://statics.dronex.cc/${result.key}`;

        // 通知 Upload 组件上传成功
        onSuccess(fileUrl, file);
      } catch (error) {
        message.error('文件上传失败');
        onError(error);
      }
    },
    []
  );

  const handleChange = useCallback(
    (info) => {
      const { file, fileList: newFileList } = info;

      if (multiple) {
        setFileList(newFileList);
        // 如果所有文件都上传完成，调用 onChange
        const allDone = newFileList.every(
          (f) =>
            f.status === 'done' ||
            f.status === 'error' ||
            f.status === undefined
        );
        if (allDone && onChange) {
          const doneFiles = newFileList
            .filter((f) => f.status === 'done' || f.status === undefined)
            .map((f) => ({
              uid: f.uid,
              name: f.name,
              size: f.size,
              type: f.type,
              status: 'done',
              url: f.response || f.url,
            }));
          onChange(doneFiles);
        }
      } else {
        // 单文件模式
        if (file.status === 'uploading') {
          setLoading(true);
        } else if (file.status === 'done') {
          setLoading(false);
          const fileUrl = file.response || file.url;
          setFileList([
            {
              uid: file.uid,
              name: file.name,
              size: file.size,
              type: file.type,
              status: 'done',
              url: fileUrl,
            },
          ]);
          if (onChange) onChange(fileUrl, file);
        } else if (file.status === 'error' || file.status === 'removed') {
          setLoading(false);
        }
      }
    },
    [multiple, onChange]
  );

  const handleRemove = useCallback(
    (file) => {
      if (multiple) {
        const updatedFileList = fileList.filter((f) => f.uid !== file.uid);
        setFileList(updatedFileList);

        const urls = updatedFileList.filter((f) => f.status === 'done');
        if (onChange) onChange(urls);
      }
    },
    [multiple, fileList, onChange]
  );

  const isImage = useMemo(() => {
    return accept.indexOf('image/') !== -1;
  }, [accept]);

  return (
    <div className="mb-6 flex">
      {!multiple && (
        <>
          {label && (
            <label className="block text-sm font-medium text-gray-700 min-w-20 whitespace-nowrap text-left">
              {require && <span className="text-red-500 mr-1">*</span>}
              {label}
            </label>
          )}
          <div className="flex flex-col items-start">
            <Upload
              name="file"
              listType={isImage ? 'picture-card' : 'text'}
              showUploadList={false}
              disabled={disabled}
              beforeUpload={handleBeforeUpload}
              customRequest={handleCustomRequest}
              onChange={handleChange}
              accept={accept}
              className="overflow-hidden rounded-lg relative"
            >
              {isImage && (fileList[0] || value) && (
                <div className="relative w-full h-full group">
                  <Image
                    src={fileList[0]?.url || value}
                    alt="preview"
                    className="w-full aspect-square object-cover"
                    preview={false}
                  />
                  {!disabled && (
                    <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded flex items-center justify-center pointer-events-none">
                      <span className="text-white">点击更换</span>
                    </div>
                  )}
                </div>
              )}
              {!loading && isImage && !fileList[0] && !value && (
                <div className="flex flex-col items-center justify-center w-full h-full transition-colors duration-200">
                  <PlusOutlined className="text-2xl" />
                  <div className="mt-2 text-sm text-gray-500">点击上传</div>
                </div>
              )}
              {loading && isImage && (
                <div className="absolute text-gray-500">
                  <LoadingOutlined className="text-2xl" />
                </div>
              )}
              {!isImage && (
                <Button
                  icon={<UploadOutlined />}
                  loading={loading}
                  className="cursor-pointer"
                  type="primary"
                >
                  {loading ? '上传中...' : '选择文件'}
                </Button>
              )}
              {!isImage && fileList[0] && (
                <div className="flex items-center gap-2 mt-2">
                  <svg
                    className="w-5 h-5 text-blue-500 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <span className="text-sm text-slate-700">
                    {fileList[0].name}
                  </span>
                </div>
              )}
            </Upload>
            <div className="mt-2 text-left text-xs text-zinc-500 flex flex-col items-start">
              {placeholder && <span>{placeholder}</span>}
              {demoUrl && (
                <button
                  onClick={() => setDemoShow(true)}
                  className="text-blue-400 mt-1"
                >
                  查看示例
                </button>
              )}
            </div>
          </div>
        </>
      )}
      {multiple && (
        <div className="flex-1">
          <Dragger
            disabled={disabled}
            multiple
            beforeUpload={handleBeforeUpload}
            customRequest={handleCustomRequest}
            onChange={handleChange}
            onRemove={handleRemove}
            fileList={multiple ? fileList : undefined}
            showUploadList={multiple}
            accept={accept}
            style={{ display: 'block' }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined className="text-4xl text-blue-500" />
            </p>
            <p className="ant-upload-text">
              {require && <span className="text-red-500 mr-1">*</span>}
              {label}
            </p>
            <div className="text-left text-xs text-zinc-500 flex items-center justify-center">
              <span className="ant-upload-hint">
                {placeholder || `文件大小不超过 ${maxSize}MB`}，
                支持单个或批量上传
              </span>
              {demoDownloadUrl && (
                <a
                  href={demoDownloadUrl}
                  className="text-blue-400 ml-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  下载示例
                </a>
              )}
            </div>
          </Dragger>
        </div>
      )}
      {demoUrl && (
        <Image
          style={{ display: 'none' }}
          alt="示例"
          src={demoUrl}
          preview={{
            open: demoShow,
            src: demoUrl,
            onOpenChange: setDemoShow,
          }}
        />
      )}
    </div>
  );
}
